import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Plan = 'free' | 'pro' | 'unlimited' | 'enterprise'

const API = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3001'

interface User {
  id: string
  email: string
  name: string
  plan: Plan
  role?: 'user' | 'admin'
}

interface AuthState {
  user: User | null
  token: string | null
  ready: boolean
  login: (email: string, password: string) => Promise<string | null>
  register: (email: string, name: string, password: string, plan?: string) => Promise<string | null>
  logout: () => void
  upgradePlan: (plan: Plan) => Promise<void>
  checkSession: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      ready: false,

      login: async (email, password) => {
        try {
          const res = await fetch(`${API}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          })
          const data = await res.json()
          if (!res.ok) return data.error ?? 'Login failed'
          set({ user: data.user, token: data.token })
          return null
        } catch {
          return 'Network error'
        }
      },

      register: async (email, name, password, plan) => {
        try {
          const res = await fetch(`${API}/api/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, name, password, plan: plan ?? 'free' }),
          })
          const data = await res.json()
          if (!res.ok) return data.error ?? 'Registration failed'
          set({ user: data.user, token: data.token })
          return null
        } catch {
          return 'Network error'
        }
      },

      logout: () => {
        const token = get().token
        if (token) {
          fetch(`${API}/api/auth/logout`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => {})
        }
        set({ user: null, token: null })
      },

      checkSession: async () => {
        const token = get().token
        if (!token) { set({ ready: true }); return }
        try {
          const res = await fetch(`${API}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          if (res.ok) {
            const data = await res.json()
            set({ user: data.user, ready: true })
          } else {
            set({ user: null, token: null, ready: true })
          }
        } catch {
          set({ ready: true })
        }
      },

      upgradePlan: async (plan) => {
        const token = get().token
        if (!token) return
        try {
          const res = await fetch(`${API}/api/auth/plan`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ plan }),
          })
          const data = await res.json()
          if (res.ok) set({ user: data.user })
        } catch {}
      },
    }),
    { name: 'pulse-auth' }
  )
)
